/**
 * Search Parameters Module
 *
 * Utilities for encoding and managing search parameters
 */

import { encode_parameters } from "@utilities";
import { PACKAGE_TYPES } from "@alpitour/resources";

/**
 * Build search params object from component state.
 * Includes all fields needed by both flight-and-stay and only-stay flows.
 */
export function build_search_params(state) {
  return {
    package_type: state.package_type,
    occupancy: state.occupancy[0],
    keyd: state.location.keyd,
    location_label: state.location.label,
    // flight-and-stay fields
    nights: state.nights,
    departure_date: state.departure_date,
    flex_dates: state.flex_dates,
    departure_airports: Object.values(
      (state.departure_airports || []).map((a) => {
        if (a.selected) {
          return [a.iata_code];
        }
        return [];
      }),
    ).flat(),
    brands: state.brands || [],
    // only-stay fields
    checkin_date: state.checkin_date,
    checkout_date: state.checkout_date,
  };
}

/**
 * Build the locationTree (keydsMap) request body based on package type.
 *
 * Flight-and-stay: sends brands + occupancy (Senna format).
 * Only-stay: sends locale + depth (SoloLand locationTree format).
 */
export function build_keydmap_request(package_type, { brands, occupancy }) {
  if (package_type === PACKAGE_TYPES.ONLY_STAY) {
    return {
      locale: "IT",
      depth: 2,
    };
  }

  return { brands, occupancy };
}

/**
 * Build the API request body from decoded search params, based on package type.
 *
 * Flight-and-stay: passes params as-is (Senna/Alpitour format).
 * Only-stay: transforms to AlpitourV1@search format (SoloLand).
 *
 * AlpitourV1@search schema:
 *   { checkin, checkout, suggestion: { ext_id, name, match_level },
 *     occupancy: [{ adults, children: [int] }], locale, best_price, filters }
 */
export function build_search_request(params) {
  if (params.package_type === PACKAGE_TYPES.ONLY_STAY) {
    return {
      checkin: params.checkin_date,
      checkout: params.checkout_date,
      suggestion: {
        ext_id: params.keyd,
        name: params.location_label || "",
        match_level: "location",
      },
      occupancy: [
        {
          adults: params.occupancy.adults,
          children: params.occupancy.children || [],
        },
      ],
      locale: "IT",
      best_price: true,
    };
  }

  // flight-and-stay: return existing shape
  return {
    occupancy: params.occupancy,
    keyd: params.keyd,
    nights: params.nights,
    departure_date: params.departure_date,
    flex_dates: params.flex_dates,
    departure_airports: params.departure_airports,
    brands: params.brands,
  };
}

/**
 * Encode search parameters for URL/storage
 */
export function encode_search_params(search_params) {
  return encode_parameters(search_params);
}

